import { db } from "./firebase";
import { BaseService } from "./baseService";
import { logger, LOG_CATEGORIES } from "../utils/logger";
// Audit Service for Personal Data Access Tracking
// Ensures compliance with GDPR and Banco de España regulations

import { doc, collection, setDoc, getDocs, query, where, orderBy, limit as firestoreLimit, serverTimestamp, getDoc } from "firebase/firestore";
import axios from "axios";

class AuditServiceService extends BaseService {
    constructor() {
        super("auditservices", "auditService");
    }
    async logUserAction(userId, action, details = {}) {
        try {
            const auditRef = doc(collection(db, "audit_logs"));
            await setDoc(auditRef, {
                userId,
                action,
                details,
                timestamp: new Date().toISOString(),
                userRole: await this.getUserRole(userId),
                ipAddress: details.ipAddress || "unknown",
                userAgent: details.userAgent || "unknown"
            });

            // Logging local para auditoría
            logger.log("audit", "actions", {
                userId,
                action,
                details,
                userRole: await this.getUserRole(userId),
                ipAddress: details.ipAddress || "unknown",
                userAgent: details.userAgent || "unknown",
                service: "auditService"
            });
        } catch (error) {
            await logger.error(LOG_CATEGORIES.AUDITSERVICE, "Audit logging failed:", error);
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logUserAction",
                userId,
                action
            });
            // Don't throw error to avoid breaking main flow
        }
    }

    async logOpportunityAction(userId, action, opportunityId, details = {}) {
        try {
            const auditRef = doc(collection(db, "audit_logs"));
            await setDoc(auditRef, {
                userId,
                action,
                entityType: "opportunity",
                entityId: opportunityId,
                details,
                timestamp: new Date().toISOString(),
                userRole: await this.getUserRole(userId),
                ipAddress: details.ipAddress || "unknown",
                userAgent: details.userAgent || "unknown"
            });

            // Logging local para auditoría
            logger.log("audit", "actions", {
                userId,
                action,
                entityType: "opportunity",
                entityId: opportunityId,
                details,
                userRole: await this.getUserRole(userId),
                ipAddress: details.ipAddress || "unknown",
                userAgent: details.userAgent || "unknown",
                service: "auditService"
            });

            // Logging específico de oportunidades
            logger.log("opportunities", "changes", {
                userId,
                action,
                entityType: "opportunity",
                entityId: opportunityId,
                details,
                service: "auditService"
            });
        } catch (error) {
            await logger.error(LOG_CATEGORIES.AUDITSERVICE, "Opportunity audit logging failed:", error);
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logOpportunityAction",
                userId,
                action,
                opportunityId
            });
            // Don't throw error to avoid breaking main flow
        }
    }

    async getUserRole(userId) {
        try {
            const userDoc = await getDoc(doc(db, "users", userId));
            return userDoc.exists() ? userDoc.data().role : "UNKNOWN";
        } catch (error) {
            await logger.error(LOG_CATEGORIES.AUDITSERVICE, "Error getting user role for audit:", error);
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "getUserRole",
                userId
            });
            return "UNKNOWN";
        }
    }

    async logLogin(userId, details = {}) {
        try {
            await this.logUserAction(userId, "login", {
                ...details,
                category: "auth"
            });

            // Logging específico de autenticación
            logger.log("audit", "access", {
                userId,
                action: "LOGIN",
                category: "auth",
                ipAddress: details.ipAddress || "unknown",
                userAgent: details.userAgent || "unknown",
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logLogin",
                userId
            });
        }
    }

    async logLogout(userId, details = {}) {
        try {
            await this.logUserAction(userId, "logout", {
                ...details,
                category: "auth"
            });

            // Logging específico de autenticación
            logger.log("audit", "access", {
                userId,
                action: "LOGOUT",
                category: "auth",
                ipAddress: details.ipAddress || "unknown",
                userAgent: details.userAgent || "unknown",
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logLogout",
                userId
            });
        }
    }

    async logOpportunityCreated(userId, opportunityId, opportunityData) {
        try {
            await this.logOpportunityAction(userId, "create_opportunity", opportunityId, {
                opportunityTitle: opportunityData.title || opportunityData.titulo,
                amount: opportunityData.amount || opportunityData.monto,
                location: opportunityData.location || opportunityData.ubicacion,
                category: "opportunity"
            });

            // Logging específico de oportunidades
            logger.log("opportunities", "changes", {
                userId,
                action: "CREATE_OPPORTUNITY",
                entityType: "opportunity",
                entityId: opportunityId,
                opportunityTitle: opportunityData.title || opportunityData.titulo,
                amount: opportunityData.amount || opportunityData.monto,
                location: opportunityData.location || opportunityData.ubicacion,
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logOpportunityCreated",
                userId,
                opportunityId
            });
        }
    }

    async logOpportunityUpdated(userId, opportunityId, changes) {
        try {
            await this.logOpportunityAction(userId, "update_opportunity", opportunityId, {
                changes,
                category: "opportunity"
            });

            // Logging específico de oportunidades
            logger.log("opportunities", "changes", {
                userId,
                action: "UPDATE_OPPORTUNITY",
                entityType: "opportunity",
                entityId: opportunityId,
                changes,
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logOpportunityUpdated",
                userId,
                opportunityId
            });
        }
    }

    async logOpportunityApproved(userId, opportunityId, opportunityTitle) {
        try {
            await this.logOpportunityAction(userId, "approve_opportunity", opportunityId, {
                opportunityTitle,
                status: "approved",
                category: "opportunity"
            });

            // Logging específico de oportunidades
            logger.log("opportunities", "changes", {
                userId,
                action: "APPROVE_OPPORTUNITY",
                entityType: "opportunity",
                entityId: opportunityId,
                opportunityTitle,
                status: "approved",
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logOpportunityApproved",
                userId,
                opportunityId
            });
        }
    }

    async logOpportunityRejected(userId, opportunityId, opportunityTitle, reason) {
        try {
            await this.logOpportunityAction(userId, "reject_opportunity", opportunityId, {
                opportunityTitle,
                status: "rejected",
                reason,
                category: "opportunity"
            });

            // Logging específico de oportunidades
            logger.log("opportunities", "changes", {
                userId,
                action: "REJECT_OPPORTUNITY",
                entityType: "opportunity",
                entityId: opportunityId,
                opportunityTitle,
                status: "rejected",
                reason,
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logOpportunityRejected",
                userId,
                opportunityId
            });
        }
    }

    async logOpportunityDeleted(userId, opportunityId, opportunityTitle) {
        try {
            await this.logOpportunityAction(userId, "delete_opportunity", opportunityId, {
                opportunityTitle,
                category: "opportunity"
            });

            // Logging específico de oportunidades
            logger.log("opportunities", "changes", {
                userId,
                action: "DELETE_OPPORTUNITY",
                entityType: "opportunity",
                entityId: opportunityId,
                opportunityTitle,
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logOpportunityDeleted",
                userId,
                opportunityId
            });
        }
    }

    async logInvestmentCreated(userId, investmentId, investmentData) {
        try {
            await this.logUserAction(userId, "create_investment", {
                investmentId,
                opportunityId: investmentData.opportunityId,
                amount: investmentData.amount,
                category: "investment"
            });

            // Logging específico de finanzas
            logger.log("finance", "transactions", {
                userId,
                action: "CREATE_INVESTMENT",
                entityType: "investment",
                entityId: investmentId,
                opportunityId: investmentData.opportunityId,
                amount: investmentData.amount,
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logInvestmentCreated",
                userId,
                investmentId
            });
        }
    }

    async logKYCSubmitted(userId, kycData) {
        try {
            await this.logUserAction(userId, "submit_kyc", {
                clientType: kycData.clientType,
                category: "kyc"
            });

            // Logging específico de compliance
            logger.log("compliance", "kyc", {
                userId,
                action: "KYC_SUBMITTED",
                clientType: kycData.clientType,
                service: "auditService"
            });

            // Logging de PII
            logger.log("pii", "changes", {
                userId,
                action: "KYC_SUBMITTED",
                clientType: kycData.clientType,
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logKYCSubmitted",
                userId
            });
        }
    }

    async logKYCApproved(userId, kycUserId) {
        try {
            await this.logUserAction(userId, "approve_kyc", {
                kycUserId,
                category: "kyc"
            });

            // Logging específico de compliance
            logger.log("compliance", "kyc", {
                userId,
                action: "KYC_APPROVED",
                kycUserId,
                service: "auditService"
            });

            // Logging de PII
            logger.log("pii", "changes", {
                userId,
                action: "KYC_APPROVED",
                kycUserId,
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logKYCApproved",
                userId,
                kycUserId
            });
        }
    }

    async logKYCRejected(userId, kycUserId, reason) {
        try {
            await this.logUserAction(userId, "reject_kyc", {
                kycUserId,
                reason,
                category: "kyc"
            });

            // Logging específico de compliance
            logger.log("compliance", "kyc", {
                userId,
                action: "KYC_REJECTED",
                kycUserId,
                reason,
                service: "auditService"
            });

            // Logging de PII
            logger.log("pii", "changes", {
                userId,
                action: "KYC_REJECTED",
                kycUserId,
                reason,
                service: "auditService"
            });
        } catch (error) {
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logKYCRejected",
                userId,
                kycUserId
            });
        }
    }

    // Log personal data access
    async logDataAccess(userId, dataType, action, details = {}) {
        try {
            const accessRef = doc(collection(db, "data_access_logs"));
            await setDoc(accessRef, {
                userId,
                dataType, // 'kyc', 'documents', 'financial', 'personal'
                action, // 'view', 'download', 'update', 'delete', 'export'
                timestamp: serverTimestamp(),
                ipAddress: details.ipAddress || "unknown",
                userAgent: details.userAgent || "unknown",
                sessionId: details.sessionId || "unknown",
                requestedBy: details.requestedBy || userId,
                purpose: details.purpose || "business_need",
                legalBasis: details.legalBasis || "legitimate_interest",
                retentionPeriod: details.retentionPeriod || "5_years",
                details: this.sanitizeDetails(details),
            });

            // Logging local para auditoría
            logger.log("audit", "access", {
                userId,
                dataType,
                action,
                ipAddress: details.ipAddress || "unknown",
                userAgent: details.userAgent || "unknown",
                sessionId: details.sessionId || "unknown",
                requestedBy: details.requestedBy || userId,
                purpose: details.purpose || "business_need",
                legalBasis: details.legalBasis || "legitimate_interest",
                retentionPeriod: details.retentionPeriod || "5_years",
                service: "auditService"
            });

            // Logging específico por tipo de datos
            if (dataType === "kyc" || dataType === "personal") {
                logger.log("pii", "access", {
                    userId,
                    dataType,
                    action,
                    purpose: details.purpose || "business_need",
                    legalBasis: details.legalBasis || "legitimate_interest",
                    service: "auditService"
                });
            } else if (dataType === "financial") {
                logger.log("finance", "balance", {
                    userId,
                    dataType,
                    action,
                    purpose: details.purpose || "business_need",
                    legalBasis: details.legalBasis || "legitimate_interest",
                    service: "auditService"
                });
            } else if (dataType === "documents") {
                logger.log("documents", "access", {
                    userId,
                    dataType,
                    action,
                    purpose: details.purpose || "business_need",
                    legalBasis: details.legalBasis || "legitimate_interest",
                    service: "auditService"
                });
            }
        } catch (error) {
            await logger.error(LOG_CATEGORIES.AUDITSERVICE, "Error logging data access:", error);
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logDataAccess",
                userId,
                dataType,
                action
            });
        }
    }

    // Log compliance events
    async logComplianceEvent(eventType, details = {}) {
        try {
            const complianceRef = doc(collection(db, "compliance_logs"));
            await setDoc(complianceRef, {
                eventType, // 'kyc_submission', 'document_upload', 'admin_review', 'data_export'
                timestamp: serverTimestamp(),
                userId: details.userId || "system",
                ipAddress: details.ipAddress || "unknown",
                userAgent: details.userAgent || "unknown",
                regulatoryRequirement: details.regulatoryRequirement || "banco_espana_aml",
                complianceStatus: details.complianceStatus || "pending",
                details: this.sanitizeDetails(details),
            });

            // Logging local para compliance
            logger.log("compliance", "aml", {
                eventType,
                userId: details.userId || "system",
                ipAddress: details.ipAddress || "unknown",
                userAgent: details.userAgent || "unknown",
                regulatoryRequirement: details.regulatoryRequirement || "banco_espana_aml",
                complianceStatus: details.complianceStatus || "pending",
                service: "auditService"
            });
        } catch (error) {
            await logger.error(LOG_CATEGORIES.AUDITSERVICE, "Error logging compliance event:", error);
            // Logging de error local
            logger.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "auditService",
                context: "logComplianceEvent",
                eventType
            });
        }
    }

    async sendAlertToAdmin(eventType, details) {
        try {
            // Replace with your actual Slack webhook URL or alerting endpoint
            const webhookUrl = process.env.REACT_APP_SLACK_WEBHOOK_URL || "https://hooks.slack.com/services/your/webhook/url";
            await axios.post(webhookUrl, {
                text: `[ALERT] ${eventType}: ${JSON.stringify(details, null, 2)}`,
            });

            // Logging de alerta
            logger.log("system", "warning", {
                eventType,
                details,
                alertSent: true,
                service: "auditService"
            });
        } catch (err) {
            // Fallback: log to console if alert fails
            await logger.error(LOG_CATEGORIES.AUDITSERVICE, "Failed to send admin alert", err, eventType, details);
            // Logging de error local
            logger.log("system", "error", {
                error: err.message,
                stack: err.stack,
                service: "auditService",
                context: "sendAlertToAdmin",
                eventType,
                alertSent: false
            });
        }
    }

    // Sanitize details to remove sensitive information
    sanitizeDetails(details) {
        const sanitized = { ...details };

        // Remove sensitive fields
        const sensitiveFields = [
            "password", "token", "secret", "key", "ssn", "dni", "passport",
            "creditCard", "bankAccount", "cif", "taxId", "phone", "email"
        ];

        sensitiveFields.forEach(field => {
            if (sanitized[field]) {
                sanitized[field] = "[REDACTED]";
            }
        });

        // Sanitize nested objects
        Object.keys(sanitized).forEach(key => {
            if (typeof sanitized[key] === "object" && sanitized[key] !== null) {
                sanitized[key] = this.sanitizeDetails(sanitized[key]);
            }
        });

        return sanitized;
    }

    // Get audit logs for a user
    async getUserAuditLogs(userId, max = 100) {
        try {
            let q;
            if (userId === "all") {
                q = query(
                    collection(db, "audit_logs"),
                    orderBy("timestamp", "desc"),
                    firestoreLimit(max)
                );
            } else {
                q = query(
                    collection(db, "audit_logs"),
                    where("userId", "==", userId),
                    orderBy("timestamp", "desc"),
                    firestoreLimit(max)
                );
            }
      
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            await logger.error(LOG_CATEGORIES.AUDITSERVICE, "Error getting audit logs:", error);
            return [];
        }
    }
}

export const auditService = new AuditServiceService();